import { forwardRef } from 'react';
import type { ComponentPropsWithoutRef } from 'react';
import { useLanguage } from '@/chassis/ui/i18n/useLanguage';
import type { Language } from '@/chassis/ui/i18n/i18n-helper';

type I18nLabel = Record<Language, string>;

// ============================================
// Flag Types - Internal Types do Backend
// ============================================

// Flag (boolean checkbox)
interface FlagProps extends Omit<ComponentPropsWithoutRef<'input'>, 'type'> {
    label?: string;
    i18nLabel?: I18nLabel | string;
}

export const Flag = forwardRef<HTMLInputElement, FlagProps>(({ label, i18nLabel, className = '', ...props }, ref) => {
    const lang = useLanguage();
    const displayLabel = (typeof i18nLabel === 'string' ? i18nLabel : i18nLabel?.[lang]) || label;
    
    if (displayLabel) {
        return (
            <label className="flex items-center gap-2 cursor-pointer">
                <input {...props} ref={ref} className={`rounded border-gray-300 text-blue-600 shadow-sm focus:border-blue-500 focus:ring-blue-500 ${className}`} />
                <span className="text-sm text-gray-700">{displayLabel}</span>
            </label>
        );
    }

    return <input {...props} ref={ref} className={`rounded border-gray-300 text-blue-600 shadow-sm focus:border-blue-500 focus:ring-blue-500 ${className}`} />;
});
Flag.displayName = 'Flag';

// FlagSwitch (toggle switch alternative)
interface FlagSwitchProps extends Omit<ComponentPropsWithoutRef<'input'>, 'type'> {
    label?: string;
    i18nLabel?: I18nLabel | string;
}

export const FlagSwitch = forwardRef<HTMLInputElement, FlagSwitchProps>(({ label, i18nLabel, checked, className = '', ...props }, ref) => {
    const lang = useLanguage();
    const displayLabel = (typeof i18nLabel === 'string' ? i18nLabel : i18nLabel?.[lang]) || label;
    return (
        <label className="flex items-center gap-3 cursor-pointer">
            <div className="relative">
                <input {...props} ref={ref} checked={checked} className="sr-only peer" />
                <div
                    className={`
            w-11 h-6 bg-gray-200 rounded-full peer
            peer-checked:bg-blue-600
            peer-focus:ring-4 peer-focus:ring-blue-300
            transition-colors
            ${className}
          `}
                ></div>
                <div
                    className={`
            absolute left-1 top-1 w-4 h-4 bg-white rounded-full
            peer-checked:translate-x-5
            transition-transform
          `}
                ></div>
            </div>
            {displayLabel && <span className="text-sm text-gray-700">{displayLabel}</span>}
        </label>
    );
});
FlagSwitch.displayName = 'FlagSwitch';
